/**
 * Timer Settings Manager 设置管理器
 * 负责管理设置面板的交互和状态
 */

class TimerSettingsManager {
    constructor() {
        this.settingsPanel = document.getElementById('timer-settings-panel');
        this.settingsOverlay = document.getElementById('settings-overlay');
        this.settingsBtn = document.getElementById('settings-btn');

        // 模式选择 - 使用 segment-control
        this.modeSegment = document.getElementById('mode-segment');
        this.modeButtons = this.modeSegment?.querySelectorAll('.segment-option');

        // Countdown 设置
        this.countdownSettings = document.querySelector('.countdown-settings');
        this.countdownTypeSegment = document.getElementById('countdown-type-segment');
        this.countdownTypeButtons = this.countdownTypeSegment?.querySelectorAll('.segment-option');
        this.countdownNormalSettings = document.querySelector('.countdown-normal-settings');
        this.intervalSettings = document.querySelector('.interval-settings');
        this.meditationSettings = document.querySelector('.meditation-settings');
        this.hoursInput = document.getElementById('hours-input');
        this.minutesInput = document.getElementById('minutes-input');
        this.secondsInput = document.getElementById('seconds-input');
        this.intervalWorkHoursInput = document.getElementById('interval-work-hours-input');
        this.intervalWorkMinutesInput = document.getElementById('interval-work-minutes-input');
        this.intervalWorkSecondsInput = document.getElementById('interval-work-seconds-input');
        this.intervalRestHoursInput = document.getElementById('interval-rest-hours-input');
        this.intervalRestMinutesInput = document.getElementById('interval-rest-minutes-input');
        this.intervalRestSecondsInput = document.getElementById('interval-rest-seconds-input');
        this.intervalRoundsInput = document.getElementById('interval-rounds-input');
        this.meditationHoursInput = document.getElementById('meditation-hours-input');
        this.meditationMinutesInput = document.getElementById('meditation-minutes-input');
        this.meditationSecondsInput = document.getElementById('meditation-seconds-input');
        this.meditationBellHoursInput = document.getElementById('meditation-bell-hours-input');
        this.meditationBellMinutesInput = document.getElementById('meditation-bell-minutes-input');
        this.meditationBellSecondsInput = document.getElementById('meditation-bell-seconds-input');
        this.alarmSoundCheckbox = document.getElementById('alarm-sound-checkbox');

        // Stopwatch 设置
        this.stopwatchSettings = document.querySelector('.stopwatch-settings');
        this.showMillisecondsCheckbox = document.getElementById('show-milliseconds-checkbox');

        this.currentMode = 'countdown'; // 当前模式
        this.currentCountdownType = this._getInitialCountdownType();
        this.isOpen = false;
        this.userManuallyClosed = false;

        this.callbacks = {
            onModeChange: null,
            onCountdownTypeChange: null,
            onCountdownTimeChange: null,
            onIntervalSettingsChange: null,
            onMeditationSettingsChange: null,
            onShowMillisecondsChange: null,
            onAlarmSoundChange: null
        };

        this._initEventListeners();
        this._toggleModeSettings();
        this._autoOpenOnLoad();
    }

    /**
     * 初始化事件监听
     */
    _initEventListeners() {
        // 打开/关闭设置面板 - 由 shared navigation 处理
        if (this.settingsBtn) {
            this.settingsBtn.addEventListener('click', (event) => {
                event.stopPropagation();
                this.toggleSettings();
            });
        }

        // 点击遮罩关闭
        if (this.settingsOverlay) {
            this.settingsOverlay.addEventListener('click', () => {
                this.closeSettings();
            });
        }

        document.addEventListener('click', (event) => {
            if (!this.isOpen) {
                return;
            }

            const target = event.target;
            if (this.settingsPanel?.contains(target) || this.settingsBtn?.contains(target)) {
                return;
            }

            this.closeSettings();
        });

        document.addEventListener('keydown', (event) => {
            if (event.key === 'Escape' && this.isOpen) {
                this.closeSettings();
            }
        });

        // 模式切换 - 使用 segment-control 按钮
        if (this.modeButtons) {
            this.modeButtons.forEach(button => {
                button.addEventListener('click', () => {
                    // 更新按钮状态
                    this.modeButtons.forEach(btn => btn.classList.remove('active'));
                    button.classList.add('active');

                    // 切换模式
                    this.currentMode = button.getAttribute('data-mode');
                    this._toggleModeSettings();
                    this._triggerCallback('onModeChange', this.currentMode);
                });
            });
        }

        // Countdown 类型切换
        if (this.countdownTypeButtons) {
            this.countdownTypeButtons.forEach(button => {
                button.addEventListener('click', () => {
                    this.countdownTypeButtons.forEach(btn => btn.classList.remove('active'));
                    button.classList.add('active');
                    this.currentCountdownType = button.getAttribute('data-type');
                    this._toggleCountdownTypeSettings();
                    this._triggerCallback('onCountdownTypeChange', this.currentCountdownType);
                });
            });
        }

        // Countdown 时间输入
        [this.hoursInput, this.minutesInput, this.secondsInput].forEach(input => {
            if (input) {
                input.addEventListener('change', () => {
                    this._triggerCallback('onCountdownTimeChange', this.getCountdownTime());
                });
            }
        });

        // Interval 时间输入
        [
            this.intervalWorkHoursInput,
            this.intervalWorkMinutesInput,
            this.intervalWorkSecondsInput,
            this.intervalRestHoursInput,
            this.intervalRestMinutesInput,
            this.intervalRestSecondsInput,
            this.intervalRoundsInput
        ].forEach(input => {
            if (input) {
                input.addEventListener('change', () => {
                    this._triggerCallback('onIntervalSettingsChange', this.getIntervalSettings());
                });
            }
        });

        // Meditation 时间输入
        [
            this.meditationHoursInput,
            this.meditationMinutesInput,
            this.meditationSecondsInput,
            this.meditationBellHoursInput,
            this.meditationBellMinutesInput,
            this.meditationBellSecondsInput
        ].forEach(input => {
            if (input) {
                input.addEventListener('change', () => {
                    this._triggerCallback('onMeditationSettingsChange', this.getMeditationSettings());
                });
            }
        });

        // Countdown 铃声开关
        if (this.alarmSoundCheckbox) {
            this.alarmSoundCheckbox.addEventListener('change', (e) => {
                this._triggerCallback('onAlarmSoundChange', e.target.checked);
            });
        }

        // Stopwatch 毫秒显示
        if (this.showMillisecondsCheckbox) {
            this.showMillisecondsCheckbox.addEventListener('change', (e) => {
                this._triggerCallback('onShowMillisecondsChange', e.target.checked);
            });
        }
    }

    /**
     * 获取初始倒计时类型
     */
    _getInitialCountdownType() {
        const activeButton = this.countdownTypeSegment?.querySelector('.segment-option.active');
        return activeButton?.getAttribute('data-type') || 'normal';
    }

    /**
     * 切换设置面板 (由 settings-btn 控制)
     */
    toggleSettings() {
        if (this.isOpen) {
            this.closeSettings();
        } else {
            this.openSettings();
        }
    }

    /**
     * 首次加载自动打开
     */
    _autoOpenOnLoad() {
        requestAnimationFrame(() => {
            if (!this.settingsPanel) {
                return;
            }

            if (window.innerWidth < 1024) {
                return;
            }

            if (this.userManuallyClosed) {
                return;
            }

            this.isOpen = true;
            this.settingsPanel.classList.add('open');
            document.body.classList.add('settings-panel-open');
        });
    }

    /**
     * 打开设置面板
     */
    openSettings() {
        if (!this.settingsPanel) {
            return;
        }

        this.isOpen = true;
        this.userManuallyClosed = false;

        requestAnimationFrame(() => {
            this.settingsPanel.classList.add('open');
            if (this.settingsOverlay) {
                this.settingsOverlay.classList.add('active');
            }
            document.body.classList.add('settings-panel-open');
        });
    }

    /**
     * 关闭设置面板
     */
    closeSettings() {
        if (!this.settingsPanel) {
            return;
        }

        this.isOpen = false;
        if (window.innerWidth >= 1024) {
            this.userManuallyClosed = true;
        }

        requestAnimationFrame(() => {
            this.settingsPanel.classList.remove('open');
            if (this.settingsOverlay) {
                this.settingsOverlay.classList.remove('active');
            }
            setTimeout(() => {
                document.body.classList.remove('settings-panel-open');
            }, 50);
        });
    }

    /**
     * 切换模式设置显示
     */
    _toggleModeSettings() {
        if (this.currentMode === 'countdown') {
            if (this.countdownSettings) this.countdownSettings.style.display = 'block';
            if (this.stopwatchSettings) this.stopwatchSettings.style.display = 'none';
            this._toggleCountdownTypeSettings();
        } else {
            if (this.countdownSettings) this.countdownSettings.style.display = 'none';
            if (this.stopwatchSettings) this.stopwatchSettings.style.display = 'block';
        }
    }

    /**
     * 切换倒计时子类型设置显示
     */
    _toggleCountdownTypeSettings() {
        if (this.currentCountdownType === 'interval') {
            if (this.countdownNormalSettings) this.countdownNormalSettings.style.display = 'none';
            if (this.intervalSettings) this.intervalSettings.style.display = 'block';
            if (this.meditationSettings) this.meditationSettings.style.display = 'none';
            return;
        }

        if (this.currentCountdownType === 'meditation') {
            if (this.countdownNormalSettings) this.countdownNormalSettings.style.display = 'none';
            if (this.intervalSettings) this.intervalSettings.style.display = 'none';
            if (this.meditationSettings) this.meditationSettings.style.display = 'block';
            return;
        }

        if (this.countdownNormalSettings) this.countdownNormalSettings.style.display = 'block';
        if (this.intervalSettings) this.intervalSettings.style.display = 'none';
        if (this.meditationSettings) this.meditationSettings.style.display = 'none';
    }

    /**
     * 获取当前模式
     */
    getCurrentMode() {
        return this.currentMode;
    }

    /**
     * 获取当前倒计时子类型
     */
    getCountdownType() {
        return this.currentCountdownType;
    }

    /**
     * 获取 Countdown 时间设置
     */
    getCountdownTime() {
        return {
            hours: parseInt(this.hoursInput?.value || 0),
            minutes: parseInt(this.minutesInput?.value || 0),
            seconds: parseInt(this.secondsInput?.value || 0)
        };
    }

    /**
     * 获取 Interval 设置
     */
    getIntervalSettings() {
        return {
            work: {
                hours: parseInt(this.intervalWorkHoursInput?.value || 0),
                minutes: parseInt(this.intervalWorkMinutesInput?.value || 0),
                seconds: parseInt(this.intervalWorkSecondsInput?.value || 0)
            },
            rest: {
                hours: parseInt(this.intervalRestHoursInput?.value || 0),
                minutes: parseInt(this.intervalRestMinutesInput?.value || 0),
                seconds: parseInt(this.intervalRestSecondsInput?.value || 0)
            },
            rounds: Math.max(1, parseInt(this.intervalRoundsInput?.value || 1))
        };
    }

    /**
     * 获取 Meditation 设置
     */
    getMeditationSettings() {
        return {
            total: {
                hours: parseInt(this.meditationHoursInput?.value || 0),
                minutes: parseInt(this.meditationMinutesInput?.value || 0),
                seconds: parseInt(this.meditationSecondsInput?.value || 0)
            },
            interval: {
                hours: parseInt(this.meditationBellHoursInput?.value || 0),
                minutes: parseInt(this.meditationBellMinutesInput?.value || 0),
                seconds: parseInt(this.meditationBellSecondsInput?.value || 0)
            }
        };
    }

    /**
     * 设置 Countdown 时间
     */
    setCountdownTime(hours, minutes, seconds) {
        if (this.hoursInput) this.hoursInput.value = hours;
        if (this.minutesInput) this.minutesInput.value = minutes;
        if (this.secondsInput) this.secondsInput.value = seconds;
    }

    /**
     * 获取是否显示毫秒
     */
    getShowMilliseconds() {
        return this.showMillisecondsCheckbox?.checked || false;
    }

    /**
     * 获取是否开启铃声
     */
    getAlarmEnabled() {
        return this.alarmSoundCheckbox?.checked || false;
    }

    /**
     * 设置是否显示毫秒
     */
    setShowMilliseconds(show) {
        if (this.showMillisecondsCheckbox) {
            this.showMillisecondsCheckbox.checked = show;
        }
    }

    /**
     * 设置是否开启铃声
     */
    setAlarmEnabled(enabled) {
        if (this.alarmSoundCheckbox) {
            this.alarmSoundCheckbox.checked = enabled;
        }
    }

    /**
     * 注册回调函数
     */
    on(event, callback) {
        if (this.callbacks.hasOwnProperty(event)) {
            this.callbacks[event] = callback;
        }
    }

    /**
     * 触发回调函数
     */
    _triggerCallback(event, data) {
        if (this.callbacks[event] && typeof this.callbacks[event] === 'function') {
            this.callbacks[event](data);
        }
    }
}

// 导出
export default TimerSettingsManager;
